const API_BASE = '/api/compliance/capa';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';

function getAuthHeaders(): HeadersInit {
  const token = sessionStorage.getItem('impersonation_token') || localStorage.getItem('token');
  if (!token) throw new Error('No authentication token found');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() };
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(err.error || err.message || `API Error: ${res.status}`);
  }
  return res.json();
}

export interface Capa {
  id: number;
  company_id: number;
  evidence_item_id?: number;
  compliance_page_id?: number;
  title: string;
  root_cause?: string;
  corrective_action?: string;
  responsible_user_id?: number;
  responsible_name?: string;
  due_date?: string;
  status: 'open' | 'assigned' | 'in_progress' | 'resolved' | 'verified_closed';
  rejection_reason?: string;
  created_by: number;
  created_at: string;
}

export interface CapaStats {
  total: number;
  open: number;
  in_progress: number;
  resolved: number;
  overdue: number;
}

export const capaApi = {
  async list(params?: { page?: number; limit?: number; status?: string; company_id?: number }): Promise<{ capas: Capa[]; total: number }> {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.status) q.set('status', params.status);
    if (params?.company_id) q.set('company_id', String(params.company_id));
    const res = await fetch(`${API_BASE}?${q}`, { headers: getAuthHeaders() });
    return handleResponse(res);
  },

  async create(data: Partial<Capa>): Promise<Capa> {
    const res = await fetch(API_BASE, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async get(id: number): Promise<Capa> {
    const res = await fetch(`${API_BASE}/${id}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async updateStatus(id: number, status: string, notes?: string): Promise<Capa> {
    const res = await fetch(`${API_BASE}/${id}/status`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ status, notes }) });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async assign(id: number, responsibleUserId: number): Promise<Capa> {
    const res = await fetch(`${API_BASE}/${id}/assign`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ responsible_user_id: responsibleUserId }) });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async verify(id: number, notes?: string): Promise<Capa> {
    const res = await fetch(`${API_BASE}/${id}/verify`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ notes }) });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async getByEvidence(evidenceId: number): Promise<Capa[]> {
    const res = await fetch(`${API_BASE}/evidence/${evidenceId}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async getStats(companyId?: number): Promise<CapaStats> {
    const q = companyId ? `?company_id=${companyId}` : '';
    const res = await fetch(`${API_BASE}/stats${q}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },
};
